import React, { useEffect, useMemo, useState } from "react";
import {
  Text,
  StyleSheet,
  View,
  FlatList,
  ScrollView,
} from "react-native";
import {
  getCollectionById,
  saveCollection,
} from "../services/apiService";
import { useCollectionStore } from "../store/collectionStore";
import { useThemeSettings } from "../ThemeProvider";
import { ModalType, useModalStore } from "../store/modalStore";
import { useUserStore } from "../store/userStore";
import { useIsFocused, useNavigation, useRoute } from "@react-navigation/native";
import { CustomIcon } from "./CustomIcon";
import { useActiveTabStore } from "../store/activeTabStore";
import { ROUTES } from "../shared/constants/routes";
import { TrackedTouchable } from "../analytics/components";
import TourStep from "./productTours/TourStep";
import { useAudioContext } from "../providers/AudioProvider";
import getGlobalStyles from "../globalStyles";
import { useActiveArticle } from "../features/news-feed/hooks/useActiveArticle";
import { removeCollectionFromCache, useCollections, useRecommendations, useThinkoCollections } from "../services/tanStackService";
import { CollectionBubble } from "./CollectionBubble";
import { usePrefetchCollections } from "../hooks/usePrefetchCollections";
import { useTranslation } from "react-i18next";
import { useNavStore } from "../store/navStore";
import { trackInteraction } from "../analytics/analyticsUtil";
import { ACTION_TYPE, ANALYTICS_EVENT } from "../analytics/types";
import { debounce } from "../shared/helpers/utils";

function getCollectionAndTagId(collection: any): string | undefined {
  return collection?.AndTag?.id ?? collection?.andTag?.id ?? undefined;
}
export default function CollectionTabs() {
  const { theme } = useThemeSettings();
  const gStyles = useMemo(() => getGlobalStyles(theme), [theme]);
  const {
    reload,
    setReload,
    selectedCollection,
    setSelectedCollection,
    collectionList,
    setCollectionList,
    setLastActiveArticle,
    getLastActiveArticle,
    recheckCollection,
    setRecheckCollection
  } = useCollectionStore();
  const idToken = useUserStore(state => state.idToken);
  const route = useRoute();
  const isFocused = useIsFocused();
  const navigation = useNavigation<any>();
  const { modalShown, setModalShown, clearModal, modalAdditionalProps } = useModalStore();
  const activetab = useActiveTabStore((state) => state.activetab);
  const getCurrentRoute = useNavStore((s) => s.getCurrentRoute);
  const [tempCollection, setTempCollection] = useState();
  const isListenTab = activetab === ROUTES.LISTEN;
  const isOnHomeReadOrListen =
    (route as any)?.name === ROUTES.HOME &&
    (activetab === ROUTES.READ || activetab === ROUTES.LISTEN);
  const currentRouteName = getCurrentRoute()?.name;
  const isInHomeReadListenSection = currentRouteName === ROUTES.LOGIN || (
    isFocused &&
    isOnHomeReadOrListen &&
    currentRouteName === ROUTES.HOME &&
    !(route?.params as any)?.articleId);
  const { togglePlayback, stopAndUnload } =
    useAudioContext();
  const { activeArticle, setActiveArticleId } = useActiveArticle();
  const flatListRef = React.useRef<FlatList>(null);
  const { data: recommendedCollections, isFetching: isFetchingRecommended } = useRecommendations(5);
  const { t } = useTranslation();
  const { data: thinkoCollections, isFetching: isFetchingThinko } = useThinkoCollections();
  const { refetch: refetchCollections } = useCollections();
  usePrefetchCollections();

  const debouncedLoad = useMemo(() => {
    return debounce(async () => {
      if ((reload || (idToken && !isFetchingRecommended)) && !isFetchingThinko) {
        await getCollections();
        setReload(false);
      }
    }, 300);
  }, [reload, idToken, isFetchingRecommended, isFetchingThinko, recommendedCollections]);
  useEffect(() => {
    if (reload && !isFetchingThinko && !isFetchingRecommended) {
      debouncedLoad();
    }
  }, [reload, idToken, debouncedLoad, isFetchingThinko, isFetchingRecommended]);

  // to switch collection at the same article from any collection
  useEffect(() => {
    const collectionId = (route?.params as any)?.collectionId;
    if (collectionId && collectionList.length > 0) {
      checkIfCollectionPresent(collectionId);
      return;
    }
    const isSelectedInList =selectedCollection && collectionList.some((c: any) => c.id === selectedCollection.id);
    if (!collectionId && tempCollection) {
      setTempCollection(undefined);
    }

    if (!isSelectedInList && collectionList.length > 0) {
      setSelectedCollection(collectionList[0]);
    }
  }, [
    (route?.params as any)?.collectionId,
    collectionList.length,
    selectedCollection,
    tempCollection,
  ]);

  useEffect(() => {
    if (!isListenTab) return;
    if (!pendingAutoPlayRef.current) return;
    if (!activeArticle?.id) return;

    if (
      pausedArticleIdRef.current &&
      activeArticle.id === pausedArticleIdRef.current
    ) {
      return;
    }

    pendingAutoPlayRef.current = false;

    togglePlayback("play");
  }, [activeArticle?.id, isListenTab]);
  useEffect(() => {
    if (
      modalShown === ModalType.CONFIRM &&
      modalAdditionalProps?.isCollectionModal &&
      !isInHomeReadListenSection
    ) {
      clearModal();
    }
  }, [
    modalShown,
    isInHomeReadListenSection,
    clearModal,
  ]);

  const checkIfCollectionPresent = async (collectionId: string, includeThinko?: boolean) => {
    let selected = collectionList.find((c) => c.id === collectionId);
    if (!selected && !tempCollection && isInHomeReadListenSection) {
      const collection = await getCollectionById(collectionId);
       const existing =
        collectionList.find((c: any) => c.id === collectionId) ||
        collectionList.find((c: any) =>
          (c.title ?? "").trim().toLowerCase() === (collection.title ?? "").trim().toLowerCase() &&
          ((c?.SeoTags ?? c?.tags ?? []).map((t: any) => String(t.id)).sort().join(",")) ===
          ((collection?.SeoTags ?? collection?.tags ?? []).map((t: any) => String(t.id)).sort().join(","))
        );
  
      if (existing) {
        setTempCollection(undefined);
        setSelectedCollection(existing);
        setReload(true);
        return;
      }
      if (collection && isInHomeReadListenSection) {
        setModalShown(ModalType.CONFIRM, {
          isCollectionModal: true,
          onConfirm: addCollection,
          onReject: () => {
            setTempCollection(collection);
            setSelectedCollection(collection);
            trackRecommendedCollectionDecision("dismiss", collection);
          },
          confirmData: collection,
          header: t("screens.common.header1"),
          message: t("screens.common.message1"),
        });
        setTempCollection(collection);

      }
    } else {
      if (selected) {
        if (isInHomeReadListenSection && includeThinko && selected.is_thinko && !selected.is_public) {
          setModalShown(ModalType.CONFIRM, {
            isCollectionModal: true,
            onConfirm: addCollection,
            onReject: () => trackRecommendedCollectionDecision("dismiss", selected),
            confirmData: selected,
            header: t("screens.common.header"),
            message: "",
          });
        } else {
          setSelectedCollection(selected);
        }
      }
    }
  };

  useEffect(() => {
    if (!selectedCollection?.id) return;
    if (!selectedCollection?.is_thinko) return;
    if (recheckCollection) {
      checkIfCollectionPresent(selectedCollection.id, true);
    }
    setRecheckCollection(false);

  }, [recheckCollection, selectedCollection?.id, setRecheckCollection]);
  const getCollections = async () => {
    const result = await refetchCollections();
    const collections = result.data ?? [];
    const requiredRecommendations = recommendedCollections?.slice(0, collections.length < 5 ? 5 - collections.length : 1) || [];
    const fullList = [...thinkoCollections || [], ...collections, ...requiredRecommendations];
    setCollectionList(fullList);
    if (selectedCollection?.id) {
      const updated = fullList.find((c: any) => c.id === selectedCollection.id);
      if (updated) {
        setSelectedCollection(updated);
      } else if (fullList.length > 0) {
        setSelectedCollection(fullList[0]);
        navigation.setParams({ collectionId: undefined, articleId: undefined });
      }
    }
  };
  const trackRecommendedCollectionDecision = (
    decision: "save" | "dismiss",
    collection: any
  ) => {
    if (!collection?.is_thinko) return;
    trackInteraction({
      event: ANALYTICS_EVENT.INTERACTION,
      action: ACTION_TYPE.CLICK,
      screen_name: ROUTES.HOME,
      component: "RecommendedCollectionConfirmModal",
      context: {
        flow: "recommended_collection",
        cta:
          decision === "save"
            ? "RECOMMENDED_COLLECTION_SAVE"
            : "RECOMMENDED_COLLECTION_DISMISS",
        collection_id: collection?.id,
        collection_title: collection?.title,
      },
    });
  };
  const addCollection = async (collection: any) => {
    const patchLoading = (loading: boolean) =>
      useModalStore.setState((state) => ({
        modalAdditionalProps: { ...state.modalAdditionalProps, isLoading: loading },
      }));
    patchLoading(true);
    try {
      const newCollection = await saveCollection({
        title: collection.title,
        tags: collection?.SeoTags ? collection.SeoTags.map((t: any) => t.id) : collection.tags.map((t: any) => t.id),
        andTag: getCollectionAndTagId(collection),
      });

      trackRecommendedCollectionDecision("save", collection);
      await removeCollectionFromCache(collection.id)
      setTempCollection(undefined);
      setCollectionList([ ...collectionList.filter((c: any) => c.id !== newCollection.id), newCollection, ]);
      setSelectedCollection(newCollection);
      setModalShown(ModalType.NONE);
      navigateToCollection(newCollection?.id);
    } catch (e) {
      patchLoading(false);
    }
  };
  const ManageCollections = async () => {
    setModalShown(ModalType.MANAGE_COLLECTIONS);
  };
  const navigateToCollection = (collectionId: string, articleId?: string) => {

    setReload(true);
    navigation.navigate(ROUTES.HOME, {
      selectedTab: activetab,
      collectionId,
      ...(articleId && { articleId }),
    });
  };

  const pendingAutoPlayRef = React.useRef(false);
  const pausedArticleIdRef = React.useRef<string | null>(null);
  let hasBoundFollowTour = false;

  // handle navigation
  const handlePress = async (collection: any) => {

    const collectionId = collection.id;
    const isDifferent = selectedCollection?.id !== collectionId;

    if (isDifferent) {
      if (selectedCollection?.id && activeArticle?.id) {
        setLastActiveArticle(selectedCollection.id, activeArticle.id);
      }
      await stopAndUnload();

      const newCollection = collectionList.find((c) => c.id === collectionId);

      setSelectedCollection(newCollection);

      const lastArticleId = getLastActiveArticle(collectionId);
      setActiveArticleId(lastArticleId ?? "");
      navigateToCollection(collectionId, lastArticleId);
      if (isListenTab) {
        pendingAutoPlayRef.current = true;
        pausedArticleIdRef.current = activeArticle?.id ?? null;
      }
    } else {
      if (isListenTab) {
        togglePlayback();
      }
    }
  };
  const listData = [tempCollection, ...collectionList].filter(Boolean);
  const visibleCollections = [tempCollection, ...collectionList]
  .filter(Boolean)
  .filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i);
  const renderItem = ({ item, index }) => {
    const bindFollowTour = !hasBoundFollowTour && Boolean(item.is_thinko);

    if (bindFollowTour) {
      hasBoundFollowTour = true;
    }

    return (
      <CollectionBubble
        isSelected={selectedCollection?.id === item.id}
        showPlayOnListen={isListenTab}
        collection={item}
        index={index}
        onBubblePress={handlePress}
        onTopRightPress={checkIfCollectionPresent}
        showTopRightIcon
        showFollowTour={bindFollowTour}
      />
    );
  }

  const AddCollectionButton = () => (
    <TourStep order={5}>
      <TrackedTouchable
        context={{ cta: "MANAGE_COLLECTIONS" }}
        id="manageCollections"
        style={styles.item}
        onPress={() => ManageCollections()}
      >
        <View style={gStyles.bubbleBorder}>
          <View
            style={{
              width: 60,
              height: 60,
              backgroundColor: theme?.colors?.primary,
              borderRadius: 30,
              padding: 4,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CustomIcon
              icon="plus"
              size={32}
              color={theme.dark ? theme?.colors?.black : theme?.colors?.white}
            />
          </View>
        </View>

        <Text
          style={[
            styles.label,
            styles.activeLabel,
            { color: theme?.colors?.primary },
          ]}
        >
          Add
        </Text>
      </TrackedTouchable>
    </TourStep>);

  return (
    <View style={styles.wrapper}>
      <ScrollView
        id="collections"
        horizontal
        showsHorizontalScrollIndicator={false}
      >
        <TourStep order={4}>
          <TrackedTouchable
            context={{ cta: "MANAGE_COLLECTIONS" }}
            id="manageCollections"
            style={styles.item}
            onPress={() => ManageCollections()}
          >
            <View style={gStyles.bubbleBorder} >
              <View
                style={{
                  width: 60,
                  height: 60,
                  backgroundColor: theme?.colors?.primary,
                  borderRadius: 30,
                  padding: 4,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CustomIcon icon="plus" size={32} color={theme.dark ? theme?.colors?.black : theme?.colors?.white} />
              </View>
            </View>

            <Text
              style={[
                styles.label,
                styles.activeLabel,
                { color: theme?.colors?.primary },
              ]}
            >
              Add
            </Text>
          </TrackedTouchable>
        </TourStep>
        {visibleCollections?.map(
          (c, index) => {
            if (!c) return null;
            const bindFollowTour = !hasBoundFollowTour && Boolean(c.is_thinko);

            if (bindFollowTour) {
              hasBoundFollowTour = true;
            }

            return (
              <CollectionBubble
                key={c.id}
                isSelected={selectedCollection?.id === c.id}
                showPlayOnListen={isListenTab}
                collection={c}
                index={index}
                onBubblePress={handlePress}
                onTopRightPress={checkIfCollectionPresent}
                showTopRightIcon
                showFollowTour={bindFollowTour}
              />
            );
          }
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    marginVertical: 10,
    paddingLeft: 20,
    flexDirection: "row",
  },

  item: {
    marginRight: 10,
    alignItems: "center",
  },

  label: {
    marginTop: 6,
    fontSize: 12,
    fontWeight: "500",
    width: 70,
    textAlign: "center",
  },

  activeLabel: {
    fontWeight: "800",
  },
  selectedImage: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
});
